<template>
  <div class="w-full h-full flex flex-col">
    <div class="flex-1">
      <!-- Header -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-6 rounded-lg mb-6">
        <!-- Breadcrumb -->
        <nav class="flex mb-4" aria-label="Breadcrumb">
          <ol class="flex items-center space-x-2 text-sm">
            <li>
              <router-link 
                :to="{ name: 'adminOpportunitiesIndex' }"
                class="text-content-300 hover:text-white button-interactive"
              >
                Oportunidades
              </router-link>
            </li>
            <li class="flex items-center">
              <svg class="flex-shrink-0 h-4 w-4 text-content-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-white">{{ isEditing ? 'Editar' : 'Crear' }}</span>
            </li>
          </ol>
        </nav>

        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div class="flex-1 min-w-0">
            <h1 class="text-2xl font-bold text-white sm:text-3xl mb-2">
              {{ isEditing ? 'Editar' : 'Crear' }} Oportunidad de Inversión
            </h1>
            <p class="text-sm text-content-200">
              {{ isEditing ? 'Modifica los detalles de la oportunidad' : 'Completa la información para crear una nueva oportunidad' }}
            </p>
          </div>
        </div>
      </div>

    <form class="space-y-6">
      <!-- Basic Information -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 sm:p-6 rounded-lg">
        <h2 class="text-lg font-semibold text-white mb-4">Información Básica</h2>

        <div class="space-y-4">
          <div class="flex flex-col">
            <input-label for="title" value="Título *" />
            <input class="w-full" id="title" type="text" v-model="form.title" placeholder="Ej: Desarrollo Residencial Zona Norte"/>
            <validation-errors :errors="errorFor('title')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="description" value="Descripción *" />
            <textarea
              class="h-32 w-full"
              id="description"
              v-model="form.description"
              placeholder="Describe el proyecto, ubicación, características principales y beneficios para los inversionistas..."
            ></textarea>
            <validation-errors :errors="errorFor('description')"></validation-errors>
          </div>
        </div>
      </div>

      <!-- Investment Type & Returns -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 sm:p-6 rounded-lg">
        <div class="flex items-center gap-2 mb-4">
          <svg class="w-6 h-6 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"></path>
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd"></path>
          </svg>
          <h2 class="text-lg font-semibold text-white">Tipo de Inversión y Retornos</h2>
        </div>

        <div class="space-y-5">
          <input-label value="¿Qué tipos de inversión estarán disponibles? *" class="mb-3" />

          <!-- Investment Type Selection with Integrated Fields -->
          <div class="space-y-4">
            <!-- Debt Option -->
            <div class="rounded-xl border-2 transition-all duration-200"
                 :class="form.enableDebt ? 'border-primary-500 bg-primary-500 bg-opacity-10' : 'border-content-600 bg-content-900 bg-opacity-30'">
              <label class="flex flex-col cursor-pointer p-4">
                <div class="flex items-start justify-between mb-2">
                  <div class="flex items-center">
                    <div class="text-2xl mr-3">💰</div>
                    <div>
                      <div class="font-semibold text-white text-base">Deuda</div>
                      <div class="text-xs text-content-300">Rendimiento fijo garantizado</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    v-model="form.enableDebt"
                    class="w-5 h-5 text-primary-500 bg-content-600 border-content-500 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div class="text-xs text-content-400">
                  ✓ Usa <span class="text-white font-medium">Tasa anual fija</span><br>
                  ✓ Ideal para inversionistas conservadores
                </div>
              </label>

              <!-- Debt Rate Input (shown when enabled) -->
              <transition name="slide-down">
                <div v-if="form.enableDebt" class="px-4 pb-4">
                  <div class="pt-3 border-t border-content-600 space-y-4">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-white">Configuración de Deuda</span>
                      <span class="text-xs text-primary-400 font-medium">Activo</span>
                    </div>

                    <!-- Tasa Fija -->
                    <div>
                      <input-label for="annualFixedRate" value="Tasa anual fija (%) *" class="mb-0" />
                      <input
                        class="w-full mt-2"
                        id="annualFixedRate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        v-model="form.annualFixedRate"
                        placeholder="Ej: 12.5"
                      />
                      <p class="text-xs text-content-400 mt-1">
                        📊 Rendimiento anual garantizado
                      </p>
                      <validation-errors :errors="errorFor('annualFixedRate')"></validation-errors>
                    </div>

                    <!-- Plazo para Deuda -->
                    <div>
                      <input-label for="termDebt" value="Plazo *" class="mb-0" />
                      <select
                        class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent mt-2"
                        id="termDebt"
                        v-model="form.termDebt"
                      >
                        <option value="">Seleccionar plazo</option>
                        <option value="12 meses">12 meses</option>
                        <option value="24 meses">24 meses</option>
                        <option value="36 meses">36 meses</option>
                        <option value="48 meses">48 meses</option>
                      </select>
                      <p class="text-xs text-content-400 mt-1">
                        ⏱️ Tiempo de duración del instrumento
                      </p>
                      <validation-errors :errors="errorFor('termDebt')"></validation-errors>
                    </div>

                    <!-- Inversión Mínima Deuda -->
                    <div>
                      <input-label for="minimumInvestmentDebt" value="Inversión mínima (MXN) *" class="mb-0" />
                      <input
                        class="w-full mt-2"
                        id="minimumInvestmentDebt"
                        type="number"
                        step="1000"
                        min="0"
                        v-model="form.minimumInvestmentDebt"
                        placeholder="100000"
                      />
                      <p class="text-xs text-content-400 mt-1">
                        💵 Monto mínimo para invertir en Deuda
                      </p>
                      <validation-errors :errors="errorFor('minimumInvestmentDebt')"></validation-errors>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Co-ownership Option -->
            <div class="rounded-xl border-2 transition-all duration-200"
                 :class="form.enableCoOwnership ? 'border-primary-500 bg-primary-500 bg-opacity-10' : 'border-content-600 bg-content-900 bg-opacity-30'">
              <label class="flex flex-col cursor-pointer p-4">
                <div class="flex items-start justify-between mb-2">
                  <div class="flex items-center">
                    <div class="text-2xl mr-3">🏢</div>
                    <div>
                      <div class="font-semibold text-white text-base">Adquisición de inmueble</div>
                      <div class="text-xs text-content-300">Rendimiento variable por plusvalía</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    v-model="form.enableCoOwnership"
                    class="w-5 h-5 text-primary-500 bg-content-600 border-content-500 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div class="text-xs text-content-400">
                  ✓ Usa <span class="text-white font-medium">TIR estimada</span><br>
                  ✓ Mayor potencial de retorno
                </div>
              </label>

              <!-- Co-ownership Rate Input (shown when enabled) -->
              <transition name="slide-down">
                <div v-if="form.enableCoOwnership" class="px-4 pb-4">
                  <div class="pt-3 border-t border-content-600 space-y-4">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-white">Configuración de Adquisición de inmueble</span>
                      <span class="text-xs text-primary-400 font-medium">Activo</span>
                    </div>

                    <!-- TIR Estimada -->
                    <div>
                      <input-label for="estimatedIRR" value="TIR estimada (%) *" class="mb-0" />
                      <input
                        class="w-full mt-2"
                        id="estimatedIRR"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        v-model="form.estimatedIRR"
                        placeholder="Ej: 18.0"
                      />
                      <p class="text-xs text-content-400 mt-1">
                        📈 Tasa interna de retorno proyectada
                      </p>
                      <validation-errors :errors="errorFor('estimatedIRR')"></validation-errors>
                    </div>

                    <!-- Plazo para Adquisición de inmueble -->
                    <div>
                      <input-label for="termEquity" value="Plazo" class="mb-0" />
                      <input
                        class="w-full mt-2 bg-content-600 cursor-not-allowed"
                        id="termEquity"
                        type="text"
                        value="Indefinido"
                        disabled
                        readonly
                      />
                      <p class="text-xs text-content-400 mt-1">
                        ♾️ Las inversiones en adquisición de inmueble no tienen plazo fijo
                      </p>
                    </div>

                    <!-- Inversión Mínima Adquisición de inmueble -->
                    <div>
                      <input-label for="minimumInvestmentEquity" value="Inversión mínima (MXN) *" class="mb-0" />
                      <input
                        class="w-full mt-2"
                        id="minimumInvestmentEquity"
                        type="number"
                        step="1000"
                        min="0"
                        v-model="form.minimumInvestmentEquity"
                        placeholder="150000"
                      />
                      <p class="text-xs text-content-400 mt-1">
                        💵 Monto mínimo para invertir en Adquisición de inmueble
                      </p>
                      <validation-errors :errors="errorFor('minimumInvestmentEquity')"></validation-errors>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Warning if none selected -->
          <div v-if="!form.enableDebt && !form.enableCoOwnership" class="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg">
            <p class="text-sm text-red-400">⚠️ Debes seleccionar al menos un tipo de inversión</p>
          </div>
        </div>
      </div>

      <!-- Investment Terms -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 sm:p-6 rounded-lg">
        <h2 class="text-lg font-semibold text-white mb-4">Términos Generales</h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex flex-col">
            <input-label for="interestPayment" value="Pago de intereses *" />
            <select
              class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
              id="interestPayment"
              v-model="form.interestPayment"
            >
              <option value="">Seleccionar</option>
              <option value="Mensual">Mensual</option>
              <option value="Trimestral">Trimestral</option>
              <option value="Semestral">Semestral</option>
              <option value="Anual">Anual</option>
              <option value="Al vencimiento">Al vencimiento</option>
            </select>
            <validation-errors :errors="errorFor('interestPayment')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="guarantee" value="Garantía *" />
            <input
              class="w-full"
              id="guarantee"
              type="text"
              v-model="form.guarantee"
              placeholder="Ej: Hipoteca 1er grado"
            />
            <validation-errors :errors="errorFor('guarantee')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="commission" value="Comisión *" />
            <input
              class="w-full"
              id="commission"
              type="text"
              v-model="form.commission"
              placeholder="Ej: 2% sobre monto invertido"
            />
            <validation-errors :errors="errorFor('commission')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="minAmount" value="Meta mínima *" />
            <input
              class="w-full"
              id="minAmount"
              type="number"
              v-model="form.minAmount"
              placeholder="1000000"
            />
            <p class="text-xs text-content-400 mt-1">Monto mínimo para activar</p>
            <validation-errors :errors="errorFor('minAmount')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="maxAmount" value="Meta máxima *" />
            <input
              class="w-full"
              id="maxAmount"
              type="number"
              v-model="form.maxAmount"
              placeholder="5000000"
            />
            <p class="text-xs text-content-400 mt-1">Objetivo de recaudación</p>
            <validation-errors :errors="errorFor('maxAmount')"></validation-errors>
          </div>
        </div>
      </div>

      <!-- Dates and Status -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-6 rounded-lg">
        <h2 class="text-lg font-semibold text-white mb-4">Fechas y Estado</h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div class="flex flex-col">
            <input-label for="publicationDate" value="Fecha de publicación *" />
            <input 
              class="w-full" 
              id="publicationDate" 
              type="date" 
              v-model="form.publicationDate" 
            />
            <validation-errors :errors="errorFor('publicationDate')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="deadline" value="Fecha límite *" />
            <input 
              class="w-full" 
              id="deadline" 
              type="date" 
              v-model="form.deadline" 
            />
            <validation-errors :errors="errorFor('deadline')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <input-label for="status" value="Estado *" />
            <select
              class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
              id="status"
              v-model="form.status"
            >
              <option value="draft" v-if="canTransitionTo('draft')">Borrador</option>
              <option value="active" v-if="canTransitionTo('active')">Activa</option>
              <option value="paused" v-if="canTransitionTo('paused')">Pausada</option>
              <option value="closed" v-if="canTransitionTo('closed')">Cerrada</option>
            </select>
            <validation-errors :errors="errorFor('status')"></validation-errors>
          </div>
        </div>
      </div>

      <!-- Images and Video -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-6 rounded-lg">
        <h2 class="text-lg font-semibold text-white mb-4">Multimedia</h2>
        
        <!-- YouTube Video -->
        <div class="mb-6">
          <div class="flex flex-col">
            <input-label for="youtubeVideoUrl" value="Video de YouTube (opcional)" />
            <input 
              class="w-full" 
              id="youtubeVideoUrl" 
              type="url" 
              v-model="form.youtubeVideoUrl" 
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <p class="text-xs text-content-300 mt-1">Pega la URL completa del video de YouTube</p>
            <validation-errors :errors="errorFor('youtubeVideoUrl')"></validation-errors>
          </div>
        </div>

        <!-- Images -->
        <div class="flex items-center mt-4">
          <!-- Initial upload when no images -->
          <div v-if="!imagePreview" class="w-full">
            <input
              class="hidden"
              type="file"
              name="image"
              id="image"
              ref="image"
              accept="image/*"
              multiple
              @change="getImages"
            />

            <label for="image" class="cursor-pointer secondary-button">
              + Agregar Imágenes
            </label>

            <validation-errors
              class="mt-2 block"
              :errors="errorFor('images')"
            ></validation-errors>
          </div>

          <!-- Show images with add more button -->
          <div v-else class="w-full">
            <div class="flex items-center flex-wrap gap-4 mb-3">
              <div v-for="(image, index) in imagePreviews" :key="`img-${index}`" class="relative">
                <img
                  class="rounded-lg object-cover object-center w-24 h-24 border border-content-500"
                  :class="[{ 'animate-pulse': loading }]"
                  :src="image"
                  :alt="`Imagen ${index + 1}`"
                  @error="handleImageError(index)"
                  @load="handleImageLoad(index)"
                />
                <button
                  v-if="!loading"
                  class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                  @click.prevent="removeImage(index)"
                  title="Eliminar imagen"
                >
                  ×
                </button>
              </div>
            </div>
            
            <!-- Separate input for adding more images -->
            <input
              class="hidden"
              type="file"
              name="moreImages"
              id="moreImages"
              ref="moreImages"
              accept="image/*"
              multiple
              @change="addMoreImages"
            />
            <label for="moreImages" class="cursor-pointer secondary-button">
              + Agregar más imágenes
            </label>
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-6 rounded-lg">
        <h2 class="text-lg font-semibold text-white mb-4">Documentos Adjuntos</h2>
        
        <!-- Document Upload -->
        <div class="mb-6">
          <input
            class="hidden"
            type="file"
            name="documents"
            id="documents"
            ref="documents"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
            multiple
            @change="addDocuments"
          />
          
          <label for="documents" class="cursor-pointer secondary-button">
            + Agregar Documentos
          </label>
          
          <p class="text-xs text-content-300 mt-2">
            Formatos permitidos: PDF, Word, Excel, PowerPoint, imágenes (JPG, PNG), texto. Máximo 10MB por archivo.
          </p>
        </div>

        <!-- Documents List -->
        <div v-if="documents.length > 0" class="space-y-4">
          <div
            v-for="(document, index) in documents"
            :key="`doc-${index}`"
            class="flex items-start gap-4 p-4 bg-content-600 rounded-lg border border-content-500"
          >
            <!-- Document Icon -->
            <div class="flex-shrink-0">
              <svg v-if="document.isPDF" class="w-8 h-8 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
              </svg>
              <svg v-else-if="document.isImage" class="w-8 h-8 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
              </svg>
              <svg v-else class="w-8 h-8 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
              </svg>
            </div>

            <!-- Document Info -->
            <div class="flex-1 min-w-0">
              <!-- Document Type, Title and Visibility -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <input-label :for="`doc-title-${index}`" value="Título (opcional)" />
                  <input
                    :id="`doc-title-${index}`"
                    type="text"
                    v-model="document.title"
                    class="w-full text-sm"
                    :placeholder="document.filename"
                  />
                </div>
                <div>
                  <input-label :for="`doc-type-${index}`" value="Tipo de documento" />
                  <select
                    :id="`doc-type-${index}`"
                    v-model="document.documentType"
                    class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent text-sm"
                  >
                    <option value="">Seleccionar tipo</option>
                    <option value="presentation">📊 Presentación</option>
                    <option value="financial">💰 Financiero</option>
                    <option value="legal">⚖️ Legal</option>
                    <option value="technical">🔧 Técnico</option>
                    <option value="other">📄 Otro</option>
                  </select>
                </div>
                <div>
                  <input-label :for="`doc-visibility-${index}`" value="Visibilidad *" />
                  <select
                    :id="`doc-visibility-${index}`"
                    v-model="document.isPublic"
                    class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent text-sm"
                  >
                    <option :value="false">🔒 Solo administradores</option>
                    <option :value="true">👁️ Visible para clientes</option>
                  </select>
                </div>
              </div>

              <!-- Description -->
              <div class="mb-3">
                <input-label :for="`doc-desc-${index}`" value="Descripción (opcional)" />
                <textarea
                  :id="`doc-desc-${index}`"
                  v-model="document.description"
                  class="w-full text-sm h-16"
                  placeholder="Breve descripción del contenido del documento"
                ></textarea>
              </div>

              <!-- File details -->
              <div class="flex items-center gap-4 text-xs text-content-300">
                <span class="font-medium text-white">📄 {{ document.filename }}</span>
                <span class="bg-content-700 px-2 py-1 rounded">{{ document.formattedSize }}</span>
                <span v-if="document.isPDF" class="bg-red-600 text-white px-2 py-1 rounded text-xs">PDF</span>
                <span v-else-if="document.isImage" class="bg-green-600 text-white px-2 py-1 rounded text-xs">IMG</span>
                <span v-else class="bg-blue-600 text-white px-2 py-1 rounded text-xs">DOC</span>
              </div>
            </div>

            <!-- Remove Button -->
            <button
              type="button"
              @click="removeDocument(index)"
              class="flex-shrink-0 p-2 text-red-400 hover:text-red-300 button-interactive"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <primary-button
        class="mt-12 w-full sm:w-96"
        :loading="loading"
        @buttonClicked="save"
      >
        {{ isEditing ? "Actualizar" : "Crear" }} Oportunidad
      </primary-button>
      </form>
    </div>
  </div>
</template>

<script>
import api from "../../../../api";
import validationErrors from "../../../../mixins/validationErrors";
import { is422 } from "../../../../utils/response";

export default {
  name: "OpportunityForm",

  data() {
    return {
      form: {
        title: "",
        description: "",
        annualFixedRate: null,
        estimatedIRR: null,
        enableDebt: true,
        enableCoOwnership: false,
        termDebt: "",
        minimumInvestmentDebt: null,
        minimumInvestmentEquity: null,
        minAmount: null,
        maxAmount: null,
        interestPayment: "",
        commission: "",
        guarantee: "",
        publicationDate: new Date().toISOString().split('T')[0],
        deadline: "",
        status: "draft",
        intentionAmount: 0,
        closedAmount: 0,
        investorCount: 0,
        youtubeVideoUrl: ""
      },
      loading: false,
      isEditing: false,
      imagePreviews: [],
      newImages: [],
      originalImages: [], // Track original images from server
      imagesToDelete: [], // Track which original images to delete
      documents: [],
      newDocuments: [],
      originalStatus: 'draft',
      hasActiveInvestments: false,
    };
  },

  mixins: [validationErrors],

  created() {
    // Keep the same tab selection to avoid losing user navigation context
    this.$store.commit("setSelectedTab", "adminOpportunities");
    if (this.$route.params.id) {
      this.isEditing = true;
      this.loadOpportunity(this.$route.params.id);
    }
  },

  computed: {
    imagePreview() {
      return this.imagePreviews.length > 0;
    }
  },

  methods: {
    async loadOpportunity(id) {
      try {
        const response = await api.opportunities.getById(id);
        
        // The backend returns { data: opportunity }, so we need to access .data
        const opportunity = response.data;

        // Map ONLY the form fields from server data (exclude virtual fields and relations)
        this.form = {
          id: opportunity.id,
          title: opportunity.title,
          description: opportunity.description,
          annualFixedRate: opportunity.annualFixedRate,
          estimatedIRR: opportunity.estimatedIRR,
          enableDebt: opportunity.enableDebt,
          enableCoOwnership: opportunity.enableCoOwnership,
          termDebt: opportunity.term || "",
          minimumInvestmentDebt: opportunity.minimumInvestmentDebt,
          minimumInvestmentEquity: opportunity.minimumInvestmentEquity,
          minAmount: opportunity.minAmount,
          maxAmount: opportunity.maxAmount,
          interestPayment: opportunity.interestPayment,
          commission: opportunity.commission,
          guarantee: opportunity.guarantee,
          publicationDate: opportunity.publicationDate ?
            new Date(opportunity.publicationDate).toISOString().split('T')[0] : '',
          deadline: opportunity.deadline ?
            new Date(opportunity.deadline).toISOString().split('T')[0] : '',
          status: opportunity.status,
          intentionAmount: opportunity.intentionAmount || 0,
          closedAmount: opportunity.closedAmount || 0,
          investorCount: opportunity.investorCount || 0,
          youtubeVideoUrl: opportunity.youtubeVideoUrl || ''
        };
        
        // Store original status for transition validation
        this.originalStatus = opportunity.status;
        
        // Check if there are active investments
        this.hasActiveInvestments = (parseFloat(opportunity.intentionAmount) || 0) > 0 || 
                                   (parseFloat(opportunity.closedAmount) || 0) > 0;
        
        // Always initialize image arrays
        this.imagePreviews = [];
        this.newImages = []; // Reset new images when loading existing opportunity
        this.originalImages = []; // Reset original images
        this.imagesToDelete = []; // Reset images to delete
        
        if (opportunity.images && opportunity.images.length > 0) {
          
          // Process and store original images
          const processedImages = opportunity.images.map((image, index) => {
            let imageUrl = '';
            
            // If image is a string (URL), use it directly
            if (typeof image === 'string') {
              imageUrl = image;
            }
            // If image is an object with URL property
            else if (image && image.url) {
              imageUrl = image.url;
            }
            // If image has other properties, try to construct URL
            else if (image && (image.filename || image.path)) {
              imageUrl = image.filename || image.path;
            }
            else {
              imageUrl = image;
            }
            
            // Validate URL and add fallback handling
            this.preloadImage(imageUrl);
            return imageUrl;
          });
          
          // Set both arrays
          this.originalImages = [...processedImages];
          this.imagePreviews = [...processedImages];
        }
        
        // Always initialize documents array
        this.documents = [];
        
        // Load existing documents
        if (opportunity.documents && opportunity.documents.length > 0) {
          this.documents = opportunity.documents.map(doc => ({
            id: doc.id,
            filename: doc.filename || doc.name || 'Documento sin nombre',
            title: doc.title || '',
            description: doc.description || '',
            documentType: doc.documentType || doc.type || '',
            isPublic: doc.isPublic !== undefined ? doc.isPublic : true,
            formattedSize: this.formatFileSize(doc.fileSize || doc.size || 0),
            isPDF: doc.mimeType === 'application/pdf' || (doc.filename && doc.filename.toLowerCase().endsWith('.pdf')),
            isImage: doc.mimeType ? doc.mimeType.startsWith('image/') : false,
            isNew: false,
            existing: true
          }));
        }
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al cargar la oportunidad");
      }
    },

    getImages(e) {
      const files = Array.from(e.target.files);
      
      files.forEach(file => {
        if (file) {
          let url = URL.createObjectURL(file);
          let img = new Image();

          img.onload = () => {
            URL.revokeObjectURL(url);
            let reader = new FileReader();
            reader.onload = (e) => {
              this.newImages.push(file);
              this.imagePreviews.push(e.target.result);
            };
            reader.readAsDataURL(file);
          };

          img.onerror = () => {
            URL.revokeObjectURL(url);
            alert("No se pudo cargar la imagen");
          };

          img.src = url;
        }
      });
    },

    addMoreImages(e) {
      const files = Array.from(e.target.files);
      
      files.forEach(file => {
        if (file) {
          let url = URL.createObjectURL(file);
          let img = new Image();

          img.onload = () => {
            URL.revokeObjectURL(url);
            let reader = new FileReader();
            reader.onload = (e) => {
              this.newImages.push(file);
              this.imagePreviews.push(e.target.result);
            };
            reader.readAsDataURL(file);
          };

          img.onerror = () => {
            URL.revokeObjectURL(url);
            alert("No se pudo cargar la imagen");
          };

          img.src = url;
        }
      });
      
      // Reset the input so the same files can be selected again if needed
      e.target.value = '';
    },

    removeImage(index) {
      const imageUrl = this.imagePreviews[index];
      
      // Remove from preview array
      this.imagePreviews.splice(index, 1);
      
      if (this.isEditing) {
        // Check if this is an original image or a new image
        const originalIndex = this.originalImages.indexOf(imageUrl);
        
        if (originalIndex !== -1) {
          // This is an original image - add to deletion list
          if (!this.imagesToDelete.includes(imageUrl)) {
            this.imagesToDelete.push(imageUrl);
          }
        } else {
          // This is a new image - remove from newImages array
          // Calculate the position of this image in the newImages array
          const originalImagesRemaining = this.originalImages.length - this.imagesToDelete.length;
          const newImageIndex = index - originalImagesRemaining;
          
          if (newImageIndex >= 0 && newImageIndex < this.newImages.length) {
            this.newImages.splice(newImageIndex, 1);
          }
        }
      } else {
        // In create mode, all images are new
        if (index < this.newImages.length) {
          this.newImages.splice(index, 1);
        }
      }
    },

    handleImageError(index) {
      console.warn(`Error loading image at index ${index}:`, this.imagePreviews[index]);
      // Remove broken image instead of showing placeholder
      this.imagePreviews.splice(index, 1);
      if (index < this.newImages.length) {
        this.newImages.splice(index, 1);
      }
    },

    handleImageLoad(index) {
      // Optional: Add any success handling logic here
    },

    preloadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => {
          console.warn('Failed to preload image:', url);
          resolve(null); // Resolve with null instead of rejecting
        };
        img.src = url;
      });
    },

    // Document handling methods
    addDocuments(e) {
      const files = Array.from(e.target.files);
      
      files.forEach(file => {
        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          this.$store.dispatch("errorSnack", `El archivo ${file.name} es demasiado grande (máximo 10MB)`);
          return;
        }

        // Validate file type
        const allowedTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'text/plain',
          'image/jpeg',
          'image/jpg',
          'image/png'
        ];

        if (!allowedTypes.includes(file.type)) {
          this.$store.dispatch("errorSnack", `Tipo de archivo no permitido: ${file.name}`);
          return;
        }

        // Create document object
        const document = {
          file: file,
          filename: file.name,
          title: '',
          description: '',
          documentType: '',
          isPublic: false,
          formattedSize: this.formatFileSize(file.size),
          isPDF: file.type === 'application/pdf',
          isImage: file.type.startsWith('image/'),
          isNew: true
        };

        this.documents.push(document);
        this.newDocuments.push(file);
      });

      // Reset input
      e.target.value = '';
    },

    async removeDocument(index) {
      const document = this.documents[index];

      // If it's an existing document (saved to server), delete from server
      if (!document.isNew && document.id) {
        try {
          await api.opportunities.deleteDocument(document.id);
          this.$store.dispatch("successSnack", "Documento eliminado exitosamente");
        } catch (error) {
          console.error('Error deleting document:', error);
          this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al eliminar el documento");
          return; // Don't remove from UI if server deletion failed
        }
      }

      // Remove from UI
      this.documents.splice(index, 1);

      // If it's a new document, also remove from newDocuments array
      if (document.isNew) {
        const newDocIndex = this.newDocuments.findIndex(doc => doc.name === document.filename);
        if (newDocIndex !== -1) {
          this.newDocuments.splice(newDocIndex, 1);
        }
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    validateForm() {
      this.errors = {};

      // Required fields validation (common to all)
      const requiredFields = [
        'title', 'description',
        'minAmount', 'maxAmount',
        'interestPayment', 'commission', 'guarantee', 'deadline'
      ];

      requiredFields.forEach(field => {
        if (!this.form[field] || this.form[field] === '') {
          this.errors[field] = ['Campo requerido'];
        }
      });

      // Validate at least one investment type is enabled
      if (!this.form.enableDebt && !this.form.enableCoOwnership) {
        this.errors['enableDebt'] = ['Debes habilitar al menos un tipo de inversión'];
      }

      // Validate fields specific to Debt
      if (this.form.enableDebt) {
        if (!this.form.annualFixedRate || this.form.annualFixedRate === '') {
          this.errors['annualFixedRate'] = ['Campo requerido cuando Deuda está habilitada'];
        }
        if (!this.form.termDebt || this.form.termDebt === '') {
          this.errors['termDebt'] = ['Campo requerido cuando Deuda está habilitada'];
        }
        if (!this.form.minimumInvestmentDebt || this.form.minimumInvestmentDebt === '') {
          this.errors['minimumInvestmentDebt'] = ['Campo requerido cuando Deuda está habilitada'];
        }
      }

      // Validate fields specific to Adquisición de inmueble
      if (this.form.enableCoOwnership) {
        if (!this.form.estimatedIRR || this.form.estimatedIRR === '') {
          this.errors['estimatedIRR'] = ['Campo requerido cuando Adquisición de inmueble está habilitada'];
        }
        if (!this.form.minimumInvestmentEquity || this.form.minimumInvestmentEquity === '') {
          this.errors['minimumInvestmentEquity'] = ['Campo requerido cuando Adquisición de inmueble está habilitada'];
        }
      }

      // Business logic validations
      if (this.form.minAmount && this.form.maxAmount &&
          parseFloat(this.form.minAmount) > parseFloat(this.form.maxAmount)) {
        this.errors['maxAmount'] = ['El monto máximo debe ser mayor al mínimo'];
      }

      // Validate minimum investments don't exceed minimum goal
      if (this.form.enableDebt && this.form.minimumInvestmentDebt && this.form.minAmount &&
          parseFloat(this.form.minimumInvestmentDebt) > parseFloat(this.form.minAmount)) {
        this.errors['minimumInvestmentDebt'] = ['La inversión mínima debe ser menor o igual a la meta mínima'];
      }

      if (this.form.enableCoOwnership && this.form.minimumInvestmentEquity && this.form.minAmount &&
          parseFloat(this.form.minimumInvestmentEquity) > parseFloat(this.form.minAmount)) {
        this.errors['minimumInvestmentEquity'] = ['La inversión mínima debe ser menor o igual a la meta mínima'];
      }

      if (this.form.publicationDate && this.form.deadline &&
          new Date(this.form.deadline) <= new Date(this.form.publicationDate)) {
        this.errors['deadline'] = ['La fecha límite debe ser posterior a la fecha de publicación'];
      }

      return Object.keys(this.errors).length === 0;
    },

    save() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      if (!this.isEditing) {
        this.createOpportunity();
      } else {
        this.updateOpportunity();
      }
    },

    async createOpportunity() {
      this.loading = true;

      try {
        let response;

        // Always use FormData to avoid JSON serialization issues
        const formData = new FormData();

        // Append form data
        Object.keys(this.form).forEach(key => {
          // Skip termDebt as we'll handle it specially
          if (key === 'termDebt') return;

          // Always include boolean fields even when false
          if (key === 'enableDebt' || key === 'enableCoOwnership') {
            formData.append(key, this.form[key]);
          } else if (this.form[key] !== null && this.form[key] !== '') {
            formData.append(key, this.form[key]);
          }
        });

        // Set term based on type: termDebt for Debt, "Indefinido" for Co-ownership only
        if (this.form.enableDebt && !this.form.enableCoOwnership) {
          formData.append('term', this.form.termDebt || '');
        } else if (!this.form.enableDebt && this.form.enableCoOwnership) {
          formData.append('term', 'Indefinido');
        } else if (this.form.enableDebt && this.form.enableCoOwnership) {
          // Both enabled, use debt term
          formData.append('term', this.form.termDebt || '');
        }

        // Append images if present
        this.newImages.forEach((image, index) => {
          formData.append(`images`, image);
        });

        // Append documents with metadata if present
        if (this.newDocuments.length > 0) {
          const documentMetadata = [];
          this.newDocuments.forEach((file, index) => {
            const documentInfo = this.documents.find(doc => doc.filename === file.name);
            formData.append(`documents`, file);

            // Build metadata array
            documentMetadata.push({
              title: documentInfo?.title || '',
              description: documentInfo?.description || '',
              documentType: documentInfo?.documentType || '',
              isPublic: documentInfo?.isPublic || false
            });
          });

          // Append metadata as JSON string
          formData.append('documentMetadata', JSON.stringify(documentMetadata));
        }

        response = await api.opportunities.createWithImage(formData);
        
        this.$store.dispatch("successSnack", response.message || "Oportunidad creada exitosamente");
        this.$router.push({ name: "adminOpportunitiesIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error?.response?.data?.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al crear la oportunidad");
      }

      this.loading = false;
    },

    async updateOpportunity() {
      this.loading = true;

      try {
        let response;

        // Check if we have any files, document metadata changes, or images to delete
        const hasFiles = this.newImages.length > 0 || this.newDocuments.length > 0;
        const hasDocumentChanges = this.documents.some(doc => doc.existing);
        const hasImageDeletions = this.imagesToDelete.length > 0;
        
        if (hasFiles || hasDocumentChanges || hasImageDeletions) {
          // Determine if we need FormData (actual files) or can use JSON
          const hasActualFiles = this.newImages.length > 0 || this.newDocuments.length > 0;
          
          if (hasActualFiles) {
            // Use FormData for file uploads
            const formData = new FormData();
            
            // Append form data
            Object.keys(this.form).forEach(key => {
              // Skip termDebt as we'll handle it specially
              if (key === 'termDebt') return;

              // Always include boolean fields even when false
              if (key === 'enableDebt' || key === 'enableCoOwnership') {
                formData.append(key, this.form[key]);
              } else if (this.form[key] !== null && this.form[key] !== '') {
                formData.append(key, this.form[key]);
              }
            });

            // Set term based on type: termDebt for Debt, "Indefinido" for Co-ownership only
            if (this.form.enableDebt && !this.form.enableCoOwnership) {
              formData.append('term', this.form.termDebt || '');
            } else if (!this.form.enableDebt && this.form.enableCoOwnership) {
              formData.append('term', 'Indefinido');
            } else if (this.form.enableDebt && this.form.enableCoOwnership) {
              // Both enabled, use debt term
              formData.append('term', this.form.termDebt || '');
            }

            // Append images
            this.newImages.forEach((image, index) => {
              formData.append(`images`, image);
            });

            // Append images to delete
            this.imagesToDelete.forEach((imageUrl, index) => {
              formData.append(`imagesToDelete[${index}]`, imageUrl);
            });

            // Append new documents with metadata
            const documentMetadata = [];
            this.newDocuments.forEach((file, index) => {
              const documentInfo = this.documents.find(doc => doc.filename === file.name && doc.isNew);
              formData.append(`documents`, file);

              // Build metadata array
              documentMetadata.push({
                title: documentInfo?.title || '',
                description: documentInfo?.description || '',
                documentType: documentInfo?.documentType || '',
                isPublic: documentInfo?.isPublic || false
              });
            });

            // Append metadata as JSON string
            if (documentMetadata.length > 0) {
              formData.append('documentMetadata', JSON.stringify(documentMetadata));
            }

            // Include existing documents metadata for updates
            const existingDocuments = this.documents.filter(doc => doc.existing).map(doc => ({
              id: doc.id,
              title: doc.title || '',
              description: doc.description || '',
              documentType: doc.documentType || '',
              isPublic: doc.isPublic || false
            }));

            if (existingDocuments.length > 0) {
              formData.append('existingDocuments', JSON.stringify(existingDocuments));
            }

            response = await api.opportunities.updateWithImage(this.form.id, formData);
          } else {
            // Use JSON for metadata-only updates (like image deletions)
            // Remove null values from form data to avoid backend validation issues
            const cleanFormData = Object.fromEntries(
              Object.entries(this.form).filter(([_, value]) => value !== null)
            );

            const updateData = {
              ...cleanFormData,
              imagesToDelete: this.imagesToDelete,
              // Include existing documents metadata for updates
              existingDocuments: this.documents.filter(doc => doc.existing).map(doc => ({
                id: doc.id,
                title: doc.title || '',
                description: doc.description || '',
                documentType: doc.documentType || '',
                isPublic: doc.isPublic
              }))
            };

            response = await api.opportunities.update(this.form.id, updateData);
          }
        } else {
          // Update without files but check if we need to include document updates
          const existingDocuments = this.documents.filter(doc => doc.existing);

          // Prepare form data with term logic
          const formDataWithTerm = { ...this.form };

          // Set term based on type
          if (this.form.enableDebt && !this.form.enableCoOwnership) {
            formDataWithTerm.term = this.form.termDebt || '';
          } else if (!this.form.enableDebt && this.form.enableCoOwnership) {
            formDataWithTerm.term = 'Indefinido';
          } else if (this.form.enableDebt && this.form.enableCoOwnership) {
            formDataWithTerm.term = this.form.termDebt || '';
          }

          // Remove termDebt as it's not a database field
          delete formDataWithTerm.termDebt;

          // Remove null values from form data to avoid backend validation issues
          const cleanFormData = Object.fromEntries(
            Object.entries(formDataWithTerm).filter(([_, value]) => value !== null)
          );

          if (existingDocuments.length > 0) {
            // Include existing documents metadata in regular update
            const updateData = {
              ...cleanFormData,
              existingDocuments: existingDocuments.map(doc => ({
                id: doc.id,
                title: doc.title || '',
                description: doc.description || '',
                documentType: doc.documentType || '',
                isPublic: doc.isPublic
              }))
            };
            response = await api.opportunities.update(this.form.id, updateData);
          } else {
            // Update without files or document changes
            response = await api.opportunities.update(this.form.id, cleanFormData);
          }
        }

        this.$store.dispatch("successSnack", response.message || "Oportunidad actualizada exitosamente");
        this.$router.push({ name: "adminOpportunitiesIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error?.response?.data?.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al actualizar la oportunidad");
      }

      this.loading = false;
    },

    getStatusLabel(status) {
      const statusLabels = {
        draft: 'Borrador',
        active: 'Activa',
        paused: 'Pausada',
        closed: 'Cerrada'
      };
      return statusLabels[status] || status;
    },


    canTransitionTo(targetStatus) {
      // For new opportunities (not editing), allow all statuses
      if (!this.isEditing) {
        return true;
      }
      
      // If no active investments, allow all transitions
      if (!this.hasActiveInvestments) {
        return true;
      }
      
      // Block problematic transitions when there are active investments
      const currentStatus = this.originalStatus;
      
      // Don't allow going back to draft if there are investments and not currently draft
      if (targetStatus === 'draft' && currentStatus !== 'draft') {
        return false;
      }
      
      // Don't allow reactivating from closed state
      if (currentStatus === 'closed' && (targetStatus === 'active' || targetStatus === 'paused')) {
        return false;
      }
      
      return true;
    },
    
  },
};
</script>

<style scoped>
/* Slide down transition for investment type fields */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-enter-to,
.slide-down-leave-from {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}
</style>